import axios, { isAxiosError } from 'axios';
import cryptoJs from 'crypto-js';
import { getAccessToken } from 'apis/AuthApis/LoginAPI';
import { getRoutes } from 'apis/AuthApis/RouterAPI';
import { LOCAL_STORAGE_KEY } from 'constants/LocalStorageKeys';
import { getTokenFromLocalStorage, setDataInLocalStorage } from 'utils/LocalStorage';
import { resolveBackendUrl } from 'utils/backendUrl';
import { installOfflineInterceptor } from '../offline/replaceAxios';
import { redirectTo } from '../utils/appNavigation';

const instance = axios.create({
  baseURL: resolveBackendUrl(),
});

export const backEndUrl = resolveBackendUrl();

export const encryptData = (data) => {
  return cryptoJs.AES.encrypt(JSON.stringify(data), process.env.REACT_APP_SECRET_KEY).toString();
};

instance.interceptors.request.use(
  async function (config) {
    if (process.env.REACT_APP_MODE === 'Production') {
      if (
        config?.data !== undefined &&
        !config.url.startsWith('/common/file_upload') &&
        !config?.url.startsWith('/admin/ticket/add_edit_pdf/dynamicForm') &&
        !config?.url?.includes('generate-from-image') 
      ) {
        const en = cryptoJs.AES.encrypt(
          JSON.stringify(config?.data),
          process.env.REACT_APP_SECRET_KEY
        ).toString();
        let obj = {
          payload: en,
        };
        config.data = obj ?? {};
      }
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);
instance.interceptors.response.use(
  async function (response) {
    // Offline bridge responses are already plain JSON — never run them through decryption.
    if (response?.config?.__isOffline) return response;
    if (process.env.REACT_APP_MODE === 'Production') {
      let data = cryptoJs.AES.decrypt(response.data, process.env.REACT_APP_SECRET_KEY);
      let oData = data.toString(cryptoJs.enc.Utf8);
      response.data = JSON.parse(oData);
      if (typeof response.data === 'string') {
        const stringifiedData = JSON.parse(response.data);
        response.data = stringifiedData;
      }
    }
    return response;
  },
  async function (error) {
    const originalRequest = error.config;
    // Offline bridge responses are plain JSON — skip decryption and just propagate the error.
    if (error?.config?.__isOffline) return Promise.reject(error);
    // Cancelled or never answered: there is no response to read.
    if (!error?.response) return Promise.reject(error);
    if (process.env.REACT_APP_MODE === 'Production') {
      let data = cryptoJs.AES.decrypt(error.response.data, process.env.REACT_APP_SECRET_KEY);
      let oData = data.toString(cryptoJs.enc.Utf8);
      const stringifiedData = JSON.parse(oData);
      error.response.data = stringifiedData;
      if (isAxiosError(error) && typeof error.response.data === 'string') {
        const stringifiedData = JSON.parse(error.response.data);
        error.response.data = stringifiedData;
      }
    }
    const errorData =
      process.env.REACT_APP_MODE === 'Production' && typeof error?.response?.data === 'string'
        ? JSON.parse(error?.response?.data)
        : error?.response?.data;
    if (error.response?.status === 401 && errorData?.displayErrorMessage === 'Token has Expired') {
      originalRequest._retry = true;
      const res = await getAccessToken();
      if (res && res?.data?.accessToken) {
        setDataInLocalStorage(LOCAL_STORAGE_KEY.accessToken, res?.data?.accessToken);
        if (originalRequest.headers) {
          originalRequest.headers.authorization = getTokenFromLocalStorage();
          return instance(originalRequest);
        }
      }
    }
    if (
      error.response?.status === 403 &&
      errorData?.displayErrorMessage === "You Don't Have An Access"
    ) {
      getRoutes().then((res) => {
        setDataInLocalStorage(LOCAL_STORAGE_KEY.userRoutes, res?.data?.data);
        redirectTo('/branch');
      });
    }
    if (!errorData?.isSuccess && errorData?.displayErrorMessage === 'jwt expired') {
      localStorage.clear();
      redirectTo('/login');
    }

    if (
      error.response?.status === 403 &&
      errorData?.displayErrorMessage === 'Your account has been disabled by admin'
    ) {
      if (window.isNativeApp && window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'LOGOUT',
        }));
      }
      localStorage.clear();
      redirectTo('/login');
    }
    return Promise.reject(error);
  }
);

installOfflineInterceptor(instance);

export default instance;
